import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";

export default function LocationMultiSelect({
  locations,
  setFilteredLocations,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(locations);
  const menuRef = useRef();

  const user = useSelector((state) => state.user).user;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelected(locations);
    setFilteredLocations(locations);
  }, [locations.length]);

  let filtered = locations.filter((loc) =>
    loc.toLowerCase().includes(search.toLowerCase())
  );

  // Toggle selection
  function toggleLocation(loc) {
    setSelected((sel) =>
      sel.includes(loc) ? sel.filter((l) => l !== loc) : [...sel, loc]
    );

    // console.log(filtered);

    setFilteredLocations((sel) =>
      sel.includes(loc) ? sel.filter((l) => l !== loc) : [...sel, loc]
    );
  }

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-50 transition"
      >
        {/* Location Icon */}
        <svg
          className="w-5 h-5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            d="M10 20s6-5.686 6-10A6 6 0 104 10c0 4.314 6 10 6 10z"
            strokeWidth="2"
          />
          <circle cx="10" cy="10" r="2" strokeWidth="2" />
        </svg>
        <span className="font-semibold text-sm">{selected[0]}</span>
        {/* Caret Icon */}
        <svg
          className="w-4 h-4 ml-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            d="M6 8l4 4 4-4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute max-h-[800px] overflow-y-auto left-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-4 px-0">
          <div className="px-5 pb-2">
            <div className="text-gray-600 text-sm font-medium mb-2">
              Select multiple
            </div>
            <div className="relative">
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 text-sm"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {/* Optional: search icon inside input */}
              <svg
                className="w-4 h-4 absolute right-3 top-3 text-gray-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 20 20"
              >
                <circle cx="9" cy="9" r="7" strokeWidth="2" />
                <path d="M15 15l-3.5-3.5" strokeWidth="2" />
              </svg>
            </div>
            <div className="flex justify-between items-center px-5 mt-2">
              <button
                onClick={() => {
                  setSelected(locations);
                  setFilteredLocations(locations);
                }}
                className="text-xs text-blue-600 hover:underline"
              >
                Select All
              </button>
              <button
                onClick={() => {
                  setSelected([]);
                  setFilteredLocations([]);
                }}
                className="text-xs text-red-500 hover:underline"
              >
                Deselect All
              </button>
            </div>
          </div>
          <div className="border-t border-gray-100 my-2" />
          <div>
            <button
              className="flex items-center w-full px-5 py-2 text-sm text-gray-700 font-medium hover:bg-gray-50 transition"
              onClick={() => setSelected([...locations])}
            >
              {/* Location Icon */}
              <svg
                className="w-5 h-5 mr-2 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M10 20s6-5.686 6-10A6 6 0 104 10c0 4.314 6 10 6 10z"
                  strokeWidth="2"
                />
                <circle cx="10" cy="10" r="2" strokeWidth="2" />
              </svg>
              All Locations
            </button>
            <div className="mt-2">
              {filtered.map((loc) => (
                <button
                  key={loc}
                  className="flex items-center text-left w-full px-5 py-2 text-sm text-gray-900 hover:bg-gray-50 transition"
                  onClick={() => toggleLocation(loc)}
                >
                  {/* Chevron Icon */}
                  <svg
                    className="w-4 h-4 mr-2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M6 8l4 4 4-4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="flex-1 ml-1">{loc}</span>
                  {selected.includes(loc) && (
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M5 10l4 4 6-6"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
